import { DESTINATIONS, type PostDestination, type PostDestinationId } from './destinations.js';

// Publishing — per-destination CAPTION CHARACTER LIMITS for the composer's live
// counter row (composer redesign, 2026-07-20).
//
// ⚠️ THESE ARE LOCAL CONSTANTS, AND DELIBERATELY SO. A counter that updates on
// every keystroke cannot call Zernio per keystroke. So: these drive the ADVISORY
// live counter, and Zernio's `validate.validatePostLength` / `validate.validatePost`
// remain the AUTHORITATIVE pre-publish check (already wired — see
// validatePostForCompose). If a platform changes its limit, this file is the one
// place to correct, and the counter being briefly stale never lets a bad post
// through: the pre-publish validation still blocks it.
//
// SOURCES + DATE (all checked 2026-07-20 against the platforms' own publishing
// documentation — NOT against the Zernio SDK, whose types carry no limit values):
//   - Facebook feed post ....... 63,206 (Meta's documented post-message cap)
//   - Instagram feed / Reel .... 2,200  (caption field cap)
//   - Google Business post ..... 1,500  (Business Profile post summary cap)
//
// ⚠️ STORIES TAKE NO CAPTION — the limit is `null`, not a number.
// Facebook Page Stories publish a photo/video with NO message field, and
// Instagram's Content Publishing API does not accept `caption` for STORIES
// media. So the base caption is not published on a story surface at all; a
// counter with a number would imply otherwise. The composer therefore shows
// "no caption" for story destinations rather than a fake budget.
// FLAGGED FOR LIVE VERIFY on the first real story publish: if Zernio turns out
// to render the caption as a story text overlay, give these a real number here
// (one-line change) — nothing else reads the limit.
export const DESTINATION_CAPTION_LIMIT: Record<PostDestinationId, number | null> = {
  facebook: 63206,
  instagram: 2200,
  googlebusiness: 1500,
  facebook_story: null,
  instagram_story: null,
};

export interface CaptionCount {
  destination: PostDestination;
  /** Characters this destination's EFFECTIVE caption uses. */
  count: number;
  /** null when the surface publishes no caption (stories). */
  limit: number | null;
  /** limit - count; null when there is no limit to remain within. */
  remaining: number | null;
  /** true once the effective caption exceeds the limit. */
  over: boolean;
}

/**
 * Count a caption against ONE destination's limit.
 *
 * Counting is by JS string length, matching what the textarea reports and what
 * Zernio's `count` returns for plain text. (Astral-plane emoji count as 2 here —
 * which is what Meta's own byte-ish counting does for most cases; it is an
 * advisory counter, and disagreeing by a couple of characters at 63,206 or even
 * 2,200 has no practical consequence.)
 */
export function countForDestination(caption: string, destination: PostDestination): CaptionCount {
  const limit = DESTINATION_CAPTION_LIMIT[destination.id];
  const count = caption.length;
  return {
    destination,
    count,
    limit,
    remaining: limit === null ? null : limit - count,
    over: limit !== null && count > limit,
  };
}

/**
 * The counter row: one entry per SELECTED destination, in catalogue order, each
 * counting that destination's EFFECTIVE caption (its override when set, else the
 * base) — so a per-destination override is measured against its own limit, not
 * the base's.
 */
export function captionCounts(
  selected: PostDestinationId[],
  effectiveCaptionFor: (id: PostDestinationId) => string,
): CaptionCount[] {
  const chosen = new Set(selected);
  return DESTINATIONS.filter((d) => chosen.has(d.id)).map((d) =>
    countForDestination(effectiveCaptionFor(d.id), d),
  );
}

/** Any selected destination over its limit — drives the composer's warning state. */
export function hasOverLimit(counts: CaptionCount[]): boolean {
  return counts.some((c) => c.over);
}
